import {
  Box,
  ButtonBase,
  Chip,
  Divider,
  ListItemIcon,
  MenuItem,
  MenuList,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { UserAvatar } from './UserAvatar';

export interface UserMenuAction {
  label: string;
  icon: ReactNode;
  onClick: () => void;
}

interface UserMenuProps {
  name: string;
  email: string;
  avatarUrl?: string;
  roleLabel: string;
  actions: UserMenuAction[];
  collapsed: boolean;
}

// Presentational only — no auth-context awareness. The caller (apps/web's AppLayout) supplies
// user fields and the action list (Administration/Sign out), so this stays reusable.
export function UserMenu({ name, email, avatarUrl, roleLabel, actions, collapsed }: UserMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const avatar = <UserAvatar name={name} src={avatarUrl} size={UserAvatar.extraLarge} />;

  const triggerLabel = `${name}, ${roleLabel}`;

  const trigger = (
    <ButtonBase
      aria-label={triggerLabel}
      onClick={(e) => setAnchorEl(e.currentTarget)}
      sx={{ width: '100%', justifyContent: collapsed ? 'center' : 'flex-start', borderRadius: 1 }}
    >
      {collapsed ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>{avatar}</Box>
      ) : (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1.5, width: '100%' }}>
          {avatar}
          <Box sx={{ overflow: 'hidden', textAlign: 'left' }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {roleLabel}
            </Typography>
          </Box>
        </Stack>
      )}
    </ButtonBase>
  );

  return (
    <>
      {collapsed ? (
        <Tooltip title={name} placement="right">
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, minWidth: 240 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <UserAvatar name={name} src={avatarUrl} size={UserAvatar.extraLarge} />
            <Box sx={{ overflow: 'hidden' }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {email}
              </Typography>
            </Box>
          </Stack>
          <Chip label={roleLabel} size="small" sx={{ mt: 1.5 }} />
        </Box>
        <Divider />
        <MenuList>
          {actions.map((action) => (
            <MenuItem
              key={action.label}
              onClick={() => {
                setAnchorEl(null);
                action.onClick();
              }}
            >
              <ListItemIcon>{action.icon}</ListItemIcon>
              {action.label}
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  );
}
